import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { updateTripChatItem } from 'tripPlanner/api'
import type { TripItineraryData } from 'tripPlanner/api/types'
import { updateTripItineraryItem } from '../utils/tripListItineraryItemUpdateUtils'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'

export interface UpdateTripListBookingParams {
  itemId: number
  isBooked: boolean
  title: string
  occurredAt: string
}

export function updateItineraryBooking(itinerary: TripItineraryData | undefined, itemId: number, isBooked: boolean) {
  const itemIds = new Set([itemId])
  for (const stop of itinerary?.stops ?? []) {
    for (const connection of stop.transports ?? []) {
      for (const option of connection.options) {
        if (option.returnLeg && (option.itemId === itemId || option.returnLeg.itemId === itemId)) {
          itemIds.add(option.itemId)
          itemIds.add(option.returnLeg.itemId)
        }
      }
    }
  }
  const status = isBooked ? 'BOOKED' as const : 'NOT_BOOKED' as const
  return updateTripItineraryItem(itinerary, {
    updateItem: item => itemIds.has(item.id) ? { ...item, status } : item,
    updateTransportOption: option => itemIds.has(option.itemId) ? { ...option, status } : option,
  })
}

export function useTripListBookingUpdate(
  chatId: number | undefined,
  region: string | undefined,
  onBookingUpdated?: (params: UpdateTripListBookingParams) => void,
) {
  const intl = useIntl()
  const queryClient = useQueryClient()
  const queryKey = ChatKeys.itineraryDetail(chatId ?? 0, region)
  return useMutation({
    // The published API still uses status; the server stores manual false as null.
    mutationFn: ({ itemId, isBooked }: UpdateTripListBookingParams) => (
      updateTripChatItem(itemId, { status: isBooked ? 'BOOKED' : 'NOT_BOOKED' })
    ),
    onMutate: async(params) => {
      await queryClient.cancelQueries({ queryKey })
      const previousItinerary = queryClient.getQueryData<TripItineraryData>(queryKey)
      // Block checkout immediately; clearing a booking waits for the server to succeed.
      if (params.isBooked) {
        queryClient.setQueryData(queryKey, updateItineraryBooking(previousItinerary, params.itemId, true))
      }
      return { previousItinerary }
    },
    onSuccess: (_data, params) => {
      queryClient.setQueryData<TripItineraryData>(queryKey, previous => updateItineraryBooking(previous, params.itemId, params.isBooked))
      onBookingUpdated?.(params)
    },
    onError: (_error, _params, context) => {
      queryClient.setQueryData(queryKey, context?.previousItinerary)
      showSnackbar(intl.formatMessage({
        defaultMessage: 'Couldn’t update booking. Please try again.',
        id: 'DJJXTxCuMazoVeCcIoBh',
        description: 'Error shown when the manual booking marker could not be saved to the itinerary',
      }), 'critical')
    },
    onSettled: async() => {
      await queryClient.invalidateQueries({ queryKey })
    },
  })
}
